import { format } from "date-fns";

import { Column } from "@fronterahealth/frontera-ui-components";

import { ReportTemplateType } from "@api/graphql/types-and-hooks";
import { StatusDefinitionTooltip } from "@components/StatusLegendTooltip/StatusDefinitionTooltip";
import { StatusLegendTooltip } from "@components/StatusLegendTooltip/StatusLegendTooltip";
import { StatusTag } from "@components/StatusTag/StatusTag";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

// Column/action builders for the reports tables. Currently consumed by
// ClinicalReportsTable; AssessmentReportsTableV2 renders the same
// client/report-template/created-by/date/status shape and the same
// view+delete action but wasn't migrated here to keep this change scoped —
// it's a natural follow-up to fold it in too.
interface ReportRowBase {
  clientName: string;
  reportTemplate: ReportTemplateType | null;
  createdBy: string;
  dateCreated: Date;
  lastUpdated: Date;
  status: string;
}

export const clientNameColumn = <T extends ReportRowBase>(className: string): Column<T> => ({
  dataIndex: "clientName" as keyof T,
  key: "clientName",
  title: "Client Name",
  className,
  render: (value) => <span className="ph-no-capture">{value as string}</span>,
});

export const reportTemplateColumn = <T extends ReportRowBase>(className: string): Column<T> => ({
  dataIndex: "reportTemplate" as keyof T,
  key: "reportTemplate",
  title: "Report Template",
  className,
  render: (value) => (value as ReportTemplateType | null)?.name || "No template",
});

export const createdByColumn = <T extends ReportRowBase>(className: string): Column<T> => ({
  dataIndex: "createdBy" as keyof T,
  key: "createdBy",
  title: "Created By",
  className,
});

export const dateCreatedColumn = <T extends ReportRowBase>(className: string): Column<T> => ({
  dataIndex: "dateCreated" as keyof T,
  key: "dateCreated",
  title: "Date Created",
  className,
  render: (value) => (value ? format(value as unknown as Date, "MM/dd/yyyy") : ""),
});

export const lastUpdatedColumn = <T extends ReportRowBase>(className: string): Column<T> => ({
  dataIndex: "lastUpdated" as keyof T,
  key: "lastUpdated",
  title: "Last Updated",
  className,
  render: (value) => (value ? format(value as unknown as Date, "MM/dd/yyyy") : ""),
});

export const statusColumn = <T extends ReportRowBase>(className: string): Column<T> => ({
  dataIndex: "status" as keyof T,
  key: "status",
  title: (
    <span className="inline-flex items-center gap-1">
      Status
      <StatusLegendTooltip />
    </span>
  ),
  className,
  render: (value) => (
    <StatusDefinitionTooltip status={value as string}>
      <StatusTag status={value as string} />
    </StatusDefinitionTooltip>
  ),
});

export const buildViewDeleteReportActions = <T extends { id: string }>({
  isInternalUser,
  onRowClick,
  deleteReport,
  refetch,
}: {
  isInternalUser: boolean;
  onRowClick: (rowData: T) => void;
  deleteReport: (report: string) => Promise<unknown>;
  refetch: () => Promise<unknown>;
}) => [
  {
    label: "View",
    callback: (rowData: T) => onRowClick(rowData),
  },
  ...(isInternalUser
    ? [
        {
          label: "Delete",
          textColorClassName: "text-red-700",
          callback: async (rowData: { id: string }) => {
            try {
              await deleteReport(rowData.id);
              await refetch();
              notifySuccess("Report successfully deleted");
            } catch (err) {
              console.error("Failed to delete report", err);
              notifyError("Unable to delete report");
            }
          },
        },
      ]
    : []),
];
